import React, { useCallback, useMemo } from 'react';
import type { ProductCarouselNavigationControl } from '@noths/polaris-client-ribbons-design-system';
import { ProductCarousel } from '@noths/polaris-client-ribbons-design-system';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import { trackCarouselNavigation } from 'src/tracking/common/carousel';
import type { TrackingProductData } from 'src/tracking/common/products';
import { trackProductClick, trackProductImpression } from 'src/tracking/common/products';
import { trackLinkClick } from 'src/tracking/common/trackLinkClick';
import type { TrackingContext } from 'src/types/TrackingContext';
import { usePlpPreview } from './hooks/usePlpPreview';

export const getSeeAllLinkText = (heading: string) => `See all ${heading}`;

export interface PlpPreviewProductsProps {
  currency: CurrencyCode;
  heading: string;
  plpUrl: string;
  trackingPosition: number;
  trackingTitle: string;
}

export const PlpPreviewProducts = ({
  currency,
  heading,
  plpUrl,
  trackingPosition,
  trackingTitle,
}: PlpPreviewProductsProps) => {
  const trackingInfo: TrackingContext = useMemo(
    () => ({
      trackingContentType: 'PLP preview',
      trackingPosition,
      trackingTitle,
    }),
    [trackingPosition, trackingTitle],
  );

  const { products, productsToTrack } = usePlpPreview({ currency, plpUrl });

  const numberOfItems = productsToTrack.length;

  const onNavigation = useCallback(
    (swiped: boolean, control: ProductCarouselNavigationControl) => {
      const trackingDirection = control === 'next' ? 'next' : 'previous';
      trackCarouselNavigation(swiped, trackingDirection, { ...trackingInfo, numberOfItems });
    },
    [trackingInfo, numberOfItems],
  );

  const onProductClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, { index }: { index: number }) => {
      const product: TrackingProductData | undefined = productsToTrack[index];
      if (product) {
        trackProductClick(e, {
          ...trackingInfo,
          numberOfItems,
          product,
          productIndex: index,
        });
      }
    },
    [numberOfItems, productsToTrack, trackingInfo],
  );

  const onVisible = useCallback(() => {
    if (numberOfItems > 0) {
      trackProductImpression({ ...trackingInfo, products: productsToTrack });
    }
  }, [numberOfItems, trackingInfo, productsToTrack]);

  const onSeeAllClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      trackLinkClick(e, {
        ...trackingInfo,
        actionName: 'See all click',
        link: { href: plpUrl, index: 0, name: heading },
        numberOfItems,
      });
    },
    [heading, numberOfItems, plpUrl, trackingInfo],
  );

  return (
    <ProductCarousel
      currency={currency}
      onNavigation={onNavigation}
      onProductClick={onProductClick}
      onVisible={onVisible}
      products={products}
      subheadingLink={{ href: plpUrl, onClick: onSeeAllClick, text: getSeeAllLinkText(heading) }}
      title={heading}
    />
  );
};
